import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, effect, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, filter, map, merge, startWith } from 'rxjs';
import { PaginationStore } from '../../../../store/signal-stores/pagination.signal-store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'fdr-past-performance-ui-pagination',
  imports: [TranslateModule],
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent implements OnInit {
  readonly store = inject(PaginationStore);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  protected readonly showSizerSignal = signal(true);
  private readonly langTrigger = toSignal(
    merge(this.translate.onLangChange, this.translate.onTranslationChange).pipe(startWith(null))
  );

  readonly SIZER_OPTIONS = [10, 20, 30, 40, 50] as const;

  readonly totalItems = input.required<number>();

  readonly totalElementsLabelSignal = computed(() => {
    this.langTrigger();
    return `${this.translate.instant('PD.LABELS.TOTAL')} ${this.store.totalItems()}`;
  });

  readonly entriesSizerSignal = computed(() => {
    this.langTrigger();
    return this.SIZER_OPTIONS.map(size => ({
      labelContent: `${size} ${this.translate.instant('PD.LABELS.ITEMS')}`,
      showLeftIcon: false,
      hasBorderAbove: true,
      hasBorderBelow: true,
      showRightIcon: false,
      isSelected: size === this.store.pageSize(),
      onListElementClick: () => this.onPageSizeSelected(size),
    }));
  });

  readonly paginationElementsSignal = computed(() => {
    const baseUrl = this.router.url.split('#')[0];

    return Array.from({ length: this.store.totalPages() }, (_, i) => ({
      label: String(i + 1),
      anchorProperties: { href: `${baseUrl}#page-${i + 1}` },
    }));
  });

  constructor() {
    effect(() => {
      this.store.setTotalItems(this.totalItems());
    });
    effect(() => {
      const pageIndex = this.store.pageIndex();
      if (this.parseHash(location.hash) !== pageIndex) {
        this.resetPageIndexUrlHash();
      }
    });
  }

  ngOnInit(): void {
    this.initSyncUrlToPageIndex();
    this.initPageIndexFromUrlHash();
  }

  private parseHash(hash: string): number | null {
    const m = hash?.match(/^#?page-(\d+)$/);
    const pageNumber = m ? Number(m[1]) : NaN;
    return Number.isFinite(pageNumber) && pageNumber >= 1 ? pageNumber - 1 : null;
  }

  private initPageIndexFromUrlHash(): void {
    const initialFromHash = this.parseHash(location.hash);
    if (initialFromHash != null) {
      this.store.setPageIndex(initialFromHash);
    }
  }

  private initSyncUrlToPageIndex(): void {
    this.activatedRoute.fragment
      .pipe(
        map(f => this.parseHash(f ?? '')),
        filter((n): n is number => n != null),
        distinctUntilChanged(),
        filter(n => n >= 0 && n < this.store.totalPages()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(n => this.store.setPageIndex(n));
  }

  private resetPageIndexUrlHash(): void {
    this.router.navigate([], { fragment: `page-${this.store.pageIndex() + 1}`, queryParamsHandling: 'preserve', replaceUrl: true });
  }

  private onPageSizeSelected(size: number): void {
    this.store.setPageSize(size);
    this.store.resetPageIndex();
    this.resetPageIndexUrlHash();
    // Force re-rendering of the sizer so it appears closed after selection (workaround for dropdown not closing issue)
    this.showSizerSignal.set(false);
    setTimeout(() => this.showSizerSignal.set(true), 0);
  }
}
